import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {KatButton, KatFileUpload, KatModal, KatSpinner} from '@amzn/katal-react';
import {ATTACHMENT_ACCEPT, ATTACHMENT_CONFIG, formatMaxSizeMb} from 'src/app/config/AttachmentConfig';

/**
 * Reusable, presentational multi-file upload modal.
 *
 * Decoupled from any page or API: it only collects up to `maxFiles` files and
 * hands them back via `onConfirm`, which performs the actual uploads and returns
 * the files that FAILED. The modal keeps only those failed files staged so the
 * user can retry, and clears/closes on full success (driven by the parent via
 * the `visible` prop). The parent owns the `uploading` spinner state and any
 * page/upload `errorMessage`.
 *
 * Flow: open → add up to `maxFiles` files (each removable) → Confirm → the
 * parent uploads in parallel; succeeded files leave the list, failed ones remain.
 */
export interface UploadFileModalProps {
    /** Whether the modal is visible. */
    visible: boolean;
    /** Called when the modal should close (Cancel, backdrop, or after success). */
    onClose: () => void;
    /**
     * Called with the staged files when the user clicks Confirm. The parent performs
     * the uploads and resolves with the files that FAILED (empty array = all succeeded).
     * The modal then keeps only the failed files staged for retry.
     */
    onConfirm: (files: File[]) => Promise<File[]>;
    /** True while the parent is uploading — shows a spinner and disables controls. */
    uploading?: boolean;
    /** Error message to display (e.g. some uploads failed). Keeps the modal open. */
    errorMessage?: string | null;
    /** Modal title. Defaults to "Upload Files". */
    title?: string;
    /** Accepted file extensions (HTML accept attribute). Defaults to the attachment allowlist. */
    accept?: string;
    /** Max file size in bytes. Defaults to the attachment limit (256 MB). */
    maxSize?: number;
    /** Max file name length in characters. Defaults to the attachment limit (255). */
    maxNameLength?: number;
    /** Max number of files that can be staged at once. Defaults to the attachment limit (5). */
    maxFiles?: number;
}

const UploadFileModal: React.FC<UploadFileModalProps> = ({
    visible,
    onClose,
    onConfirm,
    uploading = false,
    errorMessage = null,
    title = 'Upload Files',
    accept = ATTACHMENT_ACCEPT,
    maxSize = ATTACHMENT_CONFIG.MAX_FILE_SIZE_BYTES,
    maxNameLength = ATTACHMENT_CONFIG.MAX_FILE_NAME_LENGTH,
    maxFiles = ATTACHMENT_CONFIG.MAX_FILES_PER_UPLOAD,
}) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    // Local validation/selection error, distinct from the parent-owned `errorMessage`
    // (which reports upload failures).
    const [fileError, setFileError] = useState<string | null>(null);
    // Ref to the KatFileUpload element. `selectedFiles` is our single source of truth,
    // so after each attach we imperatively clear the picker's own internal list to keep
    // the two in sync — WITHOUT remounting the element (a remount breaks drag-and-drop).
    const fileUploadRef = useRef<KatFileUpload.Element | null>(null);

    /** Clears the picker's internal file list so it can't diverge from `selectedFiles`. */
    const resetPicker = () => {
        fileUploadRef.current?.clearAllFiles?.();
    };

    // Allowed extensions derived from the `accept` prop (e.g. ".pdf,.png" → ['pdf','png']),
    // so the modal validates against whatever the caller configured — keeping it reusable.
    const allowedExtensions = accept
        .split(',')
        .map(s => s.trim().replace(/^\./, '').toLowerCase())
        .filter(Boolean);

    // Reset staged files + error whenever the modal is closed so it opens fresh.
    useEffect(() => {
        if (!visible) {
            setSelectedFiles([]);
            setFileError(null);
        }
    }, [visible]);

    const atCapacity = selectedFiles.length >= maxFiles;
    const canConfirm = selectedFiles.length > 0 && selectedFiles.length <= maxFiles && !uploading;

    /**
     * Translates a raw KatFileUpload error (code or message) into a user-friendly string.
     * Katal surfaces internal codes such as "KAT_OVER_MAX_SIZE" / "KAT_INVALID_TYPE" /
     * "KAT_OVER_MAX_FILES"; auditors should see plain guidance (with the actual limit),
     * not the code. Falls back to a generic message for anything unrecognized.
     */
    const friendlyUploadError = (raw?: string | null): string => {
        const code = (raw ?? '').toUpperCase();
        if (code.includes('MAX_SIZE') || code.includes('OVER_MAX_SIZE')) {
            return `File is too large. The maximum allowed size is ${formatMaxSizeMb(maxSize)} MB.`;
        }
        if (code.includes('INVALID_TYPE') || code.includes('TYPE')) {
            return `Unsupported file type. Allowed types: ${accept}.`;
        }
        if (code.includes('MAX_FILES') || code.includes('OVER_MAX_FILES')) {
            return `Too many files. You can upload up to ${maxFiles} files at a time.`;
        }
        if (code.includes('NAME') && code.includes('LENGTH')) {
            return `File name is too long. The maximum length is ${maxNameLength} characters.`;
        }
        // Unknown/other Katal error — show a generic, non-technical message.
        return 'The selected file could not be added. Please check the file type and size and try again.';
    };

    /** Returns an error message if the file violates type/size/name constraints, else null. */
    const validateFile = (file: File): string | null => {        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        if (allowedExtensions.length > 0 && !allowedExtensions.includes(ext)) {
            return `Unsupported file type. Allowed: ${accept}`;
        }
        if (maxNameLength > 0 && file.name.length > maxNameLength) {
            return `File name exceeds the maximum length of ${maxNameLength} characters`;
        }
        if (maxSize > 0 && file.size > maxSize) {
            return `File exceeds the maximum size of ${formatMaxSizeMb(maxSize)} MB`;
        }
        return null;
    };

    /**
     * Merges a freshly-picked batch into the staged list: validates each file, keeps
     * only as many valid files as remaining slots allow (first-come), and reports any
     * invalid or overflow files. Duplicates are allowed. The picker is then reset.
     */
    const addFiles = (incoming: File[]) => {
        const remaining = maxFiles - selectedFiles.length;
        const accepted: File[] = [];
        let invalidCount = 0;
        let firstInvalidMsg: string | null = null;

        for (const file of incoming) {
            const validationError = validateFile(file);
            if (validationError) {
                invalidCount += 1;
                if (!firstInvalidMsg) firstInvalidMsg = validationError;
                continue;
            }
            accepted.push(file);
        }

        const withinLimit = accepted.slice(0, Math.max(0, remaining));
        const overflowCount = accepted.length - withinLimit.length;

        if (withinLimit.length > 0) {
            setSelectedFiles(prev => [...prev, ...withinLimit]);
        }

        // Surface the most relevant message: invalid files first, then overflow.
        if (invalidCount > 0) {
            setFileError(firstInvalidMsg);
        } else if (overflowCount > 0) {
            setFileError(`Only ${maxFiles} files allowed. ${overflowCount} file(s) were not added.`);
        } else {
            setFileError(null);
        }

        // Clear the picker's own list so it can't diverge from `selectedFiles`.
        resetPicker();
    };

    const handleFilesAttached = (e: KatFileUpload.FilesAttachedEvent) => {
        const files = e.detail?.files ?? [];
        if (files.length > 0) {
            addFiles(files);
        }
    };

    // Fired by KatFileUpload when it rejects a selection (e.g. type/size violation).
    // Katal reports raw error codes (e.g. "KAT_OVER_MAX_SIZE"); translate them to a
    // user-friendly message rather than surfacing the internal code to the auditor.
    const handleFilesAttachedError = (e: KatFileUpload.FilesAttachedErrorEvent) => {
        setFileError(friendlyUploadError(e.detail?.error));
        resetPicker();
    };

    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        setFileError(null);
    };

    /** Clears every staged file (and any validation error), returning the list to empty. */
    const clearAllFiles = () => {
        setSelectedFiles([]);
        setFileError(null);
        resetPicker();
    };

    const handleConfirm = async () => {
        if (!canConfirm) {
            return;
        }
        const failed = await onConfirm(selectedFiles);
        // Keep only the files that failed so the user can retry; empty on full success.
        setSelectedFiles(failed);
    };

    const handleCancel = () => {
        if (!uploading) {
            onClose();
        }
    };

    return (
        <KatModal visible={visible} onClose={handleCancel} data-testid="upload-file-modal">
            <span slot="title">{title}</span>

            <div className="lighthouse-upload-modal-body">
                {/* Add control — hidden at capacity so no more files can be staged. */}
                {!atCapacity && (
                    <>
                        <div className="lighthouse-upload-hint" data-testid="upload-hint">
                            You can upload up to {maxFiles} files at a time
                        </div>
                        <KatFileUpload
                            ref={fileUploadRef}
                            variant="small"
                            fileView="list"
                            multiple={true}
                            accept={accept}
                            maxSize={maxSize}
                            disabled={uploading}
                            hideClearAll={true}
                            onFilesAttached={handleFilesAttached}
                            onFilesAttachedError={handleFilesAttachedError}
                            data-testid="upload-file-input"
                        />
                    </>
                )}

                {selectedFiles.length > 0 && (
                    <div className="lighthouse-upload-file-list" data-testid="upload-file-list">
                        {selectedFiles.map((file, index) => (
                            <div
                                key={`${file.name}-${index}`}
                                className="lighthouse-upload-selected"
                                data-testid={`upload-selected-file-${index}`}
                            >
                                <span className="lighthouse-upload-file-name">{file.name}</span>
                                <button
                                    type="button"
                                    className="lighthouse-upload-remove"
                                    aria-label={`Remove ${file.name}`}
                                    onClick={() => removeFile(index)}
                                    disabled={uploading}
                                    data-testid={`upload-remove-file-${index}`}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                        <div className="lighthouse-upload-count" data-testid="upload-file-count">
                            {selectedFiles.length}/{maxFiles} files
                        </div>
                        <button
                            type="button"
                            className="lighthouse-upload-clear-all"
                            onClick={clearAllFiles}
                            disabled={uploading}
                            data-testid="upload-clear-all-btn"
                        >
                            Clear all files
                        </button>
                    </div>
                )}

                {uploading && (
                    <div className="lighthouse-upload-progress" data-testid="upload-progress">
                        <KatSpinner size="small" />
                        <span>Uploading {selectedFiles.length} file(s)…</span>
                    </div>
                )}

                {fileError && (
                    <div className="lighthouse-upload-error" data-testid="upload-file-error">
                        {fileError}
                    </div>
                )}

                {errorMessage && (
                    <div className="lighthouse-upload-error" data-testid="upload-error">
                        {errorMessage}
                    </div>
                )}
            </div>

            <div slot="footer" className="kat-group-horizontal">
                <KatButton
                    variant="secondary"
                    label="Cancel"
                    onClick={handleCancel}
                    disabled={uploading}
                    data-testid="upload-cancel-btn"
                />
                <KatButton
                    label="Confirm"
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                    data-testid="upload-confirm-btn"
                />
            </div>
        </KatModal>
    );
};

export default UploadFileModal;
